import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, BadgeCheck, Users, Scale, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';

const TermsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current date for "Last updated"
  const lastUpdated = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Mobile/Shortened version
  if (isMobile && !showFullTerms) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        {/* Header */}
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo size="sm" data-testid="brand-logo-header" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
                KaiCheckIt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-lg mx-auto space-y-6">
            <div className="text-center mb-6">
              <FileText className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Terms of Use</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Last updated: {lastUpdated}</p>
            </div>

            <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-5">
                <p className="text-slate-700 dark:text-slate-200 text-sm leading-relaxed">
                  By using KaiCheckIt, you agree to these terms. KaiCheckIt is a <strong>verification and context tool</strong>, not an authority on truth.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Key Points
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                    <li>• <strong>No professional advice</strong> — Not medical, legal, or financial guidance</li>
                    <li>• <strong>No guarantees</strong> — Information may change; certainty isn't always possible</li>
                    <li>• <strong>Your responsibility</strong> — Don't rely solely on KaiCheckIt for critical decisions</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-blue-500" />
                    Verification Badge
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    The badge means a real human was verified behind content. It does <strong>not</strong> mean all content is correct or endorsed by KaiCheckIt. Badges can be revoked.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-purple-500" />
                    Liability
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    KaiCheckIt is provided "as is". We're not liable for damages from use of the Service. Use at your own risk.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-green-500" />
                    Contact
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Questions? Email <a href="mailto:Admin@Kaiventive.com" className="text-blue-600 dark:text-blue-400 underline">Admin@Kaiventive.com</a>
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="pt-4">
              <Button
                onClick={() => setShowFullTerms(true)}
                variant="outline"
                className="w-full"
              >
                Read Full Terms of Use
              </Button>
            </div>

            <div className="pt-4">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Full/Desktop version (or mobile with showFullTerms=true)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {isMobile && showFullTerms ? (
              <Button
                onClick={() => setShowFullTerms(false)}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Summary
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
                <span className="sm:hidden">Back</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <FileText className="w-16 h-16 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">Terms of Use</h1>
            <p className="text-slate-500 dark:text-slate-400">Last updated: {lastUpdated}</p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 mb-8">
              <CardContent className="pt-6">
                <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                  Welcome to KaiCheckIt. By accessing or using this website, application, or any related services (collectively, the "Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree, please do not use the Service.
                </p>
              </CardContent>
            </Card>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">1. Purpose of the Service</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">KaiCheckIt provides tools designed to help users:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2 mb-4">
                <li>Review statements and images against trusted public sources</li>
                <li>Understand whether information is supported, unclear, or unsupported by available evidence</li>
                <li>Identify whether certain content creators have opted into KaiCheckIt verification standards</li>
              </ul>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                KaiCheckIt is a verification and context tool, not an authority on truth.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">2. No Professional Advice</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">KaiCheckIt does not provide:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2 mb-4">
                <li>Medical advice, diagnosis, or treatment</li>
                <li>Legal, financial, or professional advice</li>
                <li>Instructions for action or decision-making</li>
              </ul>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                All outputs are informational only. You should always consult qualified professionals for decisions related to health, legal matters, or other specialized fields.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">3. No Guarantee of Accuracy</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">While KaiCheckIt uses reputable public sources and structured review processes:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2 mb-4">
                <li>We do not guarantee accuracy, completeness, or timeliness of any output</li>
                <li>Information may change as new evidence emerges</li>
                <li>Some topics cannot be conclusively verified</li>
              </ul>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                If information is uncertain, KaiCheckIt will indicate this clearly.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">4. User Responsibilities</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">By using KaiCheckIt, you agree to:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2 mb-4">
                <li>Provide content you have the right to share</li>
                <li>Use the Service responsibly and lawfully</li>
                <li>Not rely solely on KaiCheckIt outputs for critical decisions</li>
                <li>Not misrepresent KaiCheckIt outputs as endorsements or guarantees</li>
              </ul>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                You are responsible for how you interpret and use the information provided.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">5. KaiCheckIt Verification Badge</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">Some creators and professionals may display a KaiCheckIt Verification Badge.</p>
              
              <Card className="bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 mb-4">
                <CardContent className="pt-5">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 mb-3">The badge indicates that:</p>
                  <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                    <li>A real human has been verified behind the account or AI avatar</li>
                    <li>Identity and credentials (where applicable) have been reviewed</li>
                    <li>The creator has agreed to responsible communication standards</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800">
                <CardContent className="pt-5">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 mb-3">The badge:</p>
                  <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                    <li>Does not mean all content is correct</li>
                    <li>Does not imply endorsement by KaiCheckIt</li>
                    <li>May be revoked at any time if standards are not maintained</li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">6. Directory Listings</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">KaiCheckIt may display an optional directory of verified creators or professionals.</p>
              <p className="text-slate-600 dark:text-slate-300 mb-4">Directory listings:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                <li>Are informational only</li>
                <li>Do not constitute recommendations or endorsements</li>
                <li>May be removed at any time</li>
                <li>Are not visible to Premium users</li>
              </ul>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">7. Intellectual Property</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                All content, branding, trademarks, and software associated with KaiCheckIt are owned by or licensed to KaiCheckIt, unless otherwise stated.
              </p>
              <p className="text-slate-600 dark:text-slate-300 mb-4">You may not:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                <li>Copy, modify, or redistribute the Service</li>
                <li>Use KaiCheckIt branding without permission</li>
                <li>Misrepresent KaiCheckIt outputs or badges</li>
              </ul>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">8. Prohibited Uses</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                <li>Attempt to manipulate, reverse-engineer, or misuse the Service</li>
                <li>Use KaiCheckIt to spread misinformation or impersonate others</li>
                <li>Misuse verification badges or directory listings</li>
                <li>Attempt to override or bypass system safeguards</li>
              </ul>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">9. Service Availability</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">KaiCheckIt may:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2 mb-4">
                <li>Change, suspend, or discontinue features at any time</li>
                <li>Modify these Terms as needed</li>
                <li>Limit access to protect integrity, security, or compliance</li>
              </ul>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                Continued use after updates constitutes acceptance of revised Terms.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">10. Limitation of Liability</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-6 text-slate-600 dark:text-slate-300 space-y-2">
                <li>KaiCheckIt is provided "as is"</li>
                <li>KaiCheckIt shall not be liable for any direct or indirect damages arising from use of the Service</li>
                <li>Use of the Service is at your own risk</li>
              </ul>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">11. Privacy</h2>
              <p className="text-slate-600 dark:text-slate-300">
                Use of the Service is also governed by our <a href="/privacy" className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700 dark:hover:text-blue-300">Privacy Policy</a>, which explains how data is handled and protected.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">12. Governing Law</h2>
              <p className="text-slate-600 dark:text-slate-300">
                These Terms shall be governed by and interpreted in accordance with applicable laws of the relevant jurisdiction, without regard to conflict of law principles.
              </p>
            </section>

            <Separator className="my-8 dark:bg-slate-700" />

            {/* Section 13 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">13. Contact</h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">For questions about these Terms, please contact:</p>
              <Card className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="pt-5">
                  <a 
                    href="mailto:Admin@Kaiventive.com" 
                    className="text-blue-600 dark:text-blue-400 font-medium text-lg hover:underline"
                  >
                    Admin@Kaiventive.com
                  </a>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* CTA */}
          <div className="text-center pt-8">
            <Button
              onClick={() => navigate('/')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
            </div>
            <div className="flex gap-6">
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Privacy</a>
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
